import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Container, Grid, Card, Image, Header, Loader, Dimmer, Label,
    Button, Message, Input, Icon,
} from 'semantic-ui-react';
import moment from 'moment';
import { getEvents } from './lib/tickets';
import { buildPath } from './lib/tenant';
import CalendarView from './components/CalendarView';
import { t } from './locales';
import { formatMoney } from './lib/currency';

const DATE_FILTERS = [
    { key: 'all',   labelKey: 'eventList.dateFilterAll' },
    { key: 'week',  labelKey: 'eventList.dateFilterWeek' },
    { key: 'month', labelKey: 'eventList.dateFilterMonth' },
];

function isWithinRange(event, range) {
    const date = event.next_instance?.date_time;
    if (!date) return true;
    const d = moment(date);
    if (range === 'week')  return d.isBetween(moment(), moment().endOf('week'), null, '[]');
    if (range === 'month') return d.isBetween(moment(), moment().endOf('month'), null, '[]');
    return true;
}

function EventList() {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [activeCategory, setActiveCategory] = useState(null);
    const [dateFilter, setDateFilter] = useState('all');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'calendar'

    const { data, isLoading, isError } = useQuery({
        queryKey: ['ticketing-events'],
        queryFn: getEvents,
    });

    const events = useMemo(() => data?.events ?? [], [data]);

    const categories = useMemo(() => {
        const seen = new Set();
        return events
            .map(e => e.category)
            .filter(c => c && !seen.has(c) && seen.add(c));
    }, [events]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return events.filter(e => {
            const matchesSearch = !q ||
                e.name.toLowerCase().includes(q) ||
                (e.description || '').toLowerCase().includes(q) ||
                (e.location || '').toLowerCase().includes(q);
            const matchesCategory = !activeCategory || e.category === activeCategory;
            const matchesDate = isWithinRange(e, dateFilter);
            return matchesSearch && matchesCategory && matchesDate;
        });
    }, [events, search, activeCategory, dateFilter]);

    if (isLoading) {
        return (
            <Dimmer active inverted page>
                <Loader size="large" content={t('eventList.loading')} />
            </Dimmer>
        );
    }

    if (isError) {
        return (
            <Container className="contentContainer">
                <Message negative>
                    <Message.Header>{t('eventList.errorTitle')}</Message.Header>
                    <p>{t('eventList.errorBody')}</p>
                </Message>
            </Container>
        );
    }

    return (
        <Container className="contentContainer">
            <Header as="h1" className="sectionHeader" style={{ marginBottom: '1.5rem' }}>
                {t('eventList.title')}
            </Header>

            {/* Search + view toggle */}
            <div style={{ marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                <Input
                    icon="search"
                    iconPosition="left"
                    placeholder={t('eventList.searchPlaceholder')}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ flex: '1 1 200px', minWidth: 180 }}
                />
                <Button.Group size="small">
                    <Button
                        icon
                        active={viewMode === 'grid'}
                        onClick={() => setViewMode('grid')}
                        title={t('eventList.gridViewTitle')}
                    >
                        <Icon name="th" />
                    </Button>
                    <Button
                        icon
                        active={viewMode === 'calendar'}
                        onClick={() => setViewMode('calendar')}
                        title={t('eventList.calendarViewTitle')}
                    >
                        <Icon name="calendar alternate outline" />
                    </Button>
                </Button.Group>
            </div>

            {/* Date range filter */}
            <div style={{ marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {DATE_FILTERS.map(f => (
                    <Label
                        key={f.key}
                        as="a"
                        style={{
                            cursor: 'pointer',
                            backgroundColor: dateFilter === f.key ? 'var(--colour_menu_main)' : '#eee',
                            color: dateFilter === f.key ? '#fff' : '#333',
                        }}
                        onClick={() => setDateFilter(f.key)}
                    >
                        {t(f.labelKey)}
                    </Label>
                ))}
            </div>

            {/* Category filter */}
            {categories.length > 0 && (
                <div style={{ marginBottom: '1.25rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    <Label
                        as="a"
                        style={{
                            cursor: 'pointer',
                            backgroundColor: !activeCategory ? 'var(--colour_menu_main)' : '#eee',
                            color: !activeCategory ? '#fff' : '#333',
                        }}
                        onClick={() => setActiveCategory(null)}
                    >
                        {t('eventList.categoryAll')}
                    </Label>
                    {categories.map(cat => (
                        <Label
                            key={cat}
                            as="a"
                            style={{
                                cursor: 'pointer',
                                backgroundColor: activeCategory === cat ? 'var(--colour_menu_main)' : '#eee',
                                color: activeCategory === cat ? '#fff' : '#333',
                            }}
                            onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
                        >
                            {cat}
                        </Label>
                    ))}
                </div>
            )}

            {viewMode === 'calendar' ? (
                <CalendarView
                    events={filtered}
                    onEventClick={e => navigate(buildPath(`/events/${e.id}`))}
                />
            ) : (
                <>
                    {filtered.length === 0 && (
                        <Message info>
                            <Message.Header>{t('eventList.noEventsTitle')}</Message.Header>
                            <p>{search || activeCategory || dateFilter !== 'all' ? t('eventList.noEventsFiltered') : t('eventList.noEventsEmpty')}</p>
                        </Message>
                    )}

                    <Grid stackable columns={3}>
                        {filtered.map(event => {
                            const remaining = event.next_instance?.tickets_remaining;
                            const isSoldOut = remaining === 0;
                            const isLowStock = remaining != null && remaining > 0 && remaining <= 10;

                            return (
                                <Grid.Column key={event.id}>
                                    <Card
                                        fluid
                                        className="eventCard"
                                        onClick={() => navigate(buildPath(`/events/${event.id}`))}
                                        style={{
                                            borderRadius: 12, overflow: 'hidden', cursor: 'pointer',
                                            opacity: isSoldOut ? 0.7 : 1,
                                            position: 'relative',
                                        }}
                                    >
                                        <div style={{ position: 'relative' }}>
                                            {event.image_url ? (
                                                <Image
                                                    src={event.image_url}
                                                    wrapped
                                                    ui={false}
                                                    style={{ height: 180, objectFit: 'cover', width: '100%' }}
                                                />
                                            ) : (
                                                <div style={{
                                                    height: 180,
                                                    background: 'linear-gradient(135deg, var(--colour_menu_main), var(--colour_menu_light))',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '3rem' }}>🎟</span>
                                                </div>
                                            )}
                                            {isSoldOut && (
                                                <div style={{
                                                    position: 'absolute', top: 12, right: 12,
                                                    backgroundColor: 'rgba(0,0,0,0.65)',
                                                    color: '#fff', borderRadius: 6,
                                                    padding: '3px 10px', fontSize: '0.8rem', fontWeight: 700,
                                                }}>
                                                    {t('eventList.soldOut')}
                                                </div>
                                            )}
                                            {isLowStock && (
                                                <div style={{
                                                    position: 'absolute', top: 12, right: 12,
                                                    backgroundColor: '#e67e22',
                                                    color: '#fff', borderRadius: 6,
                                                    padding: '3px 10px', fontSize: '0.8rem', fontWeight: 700,
                                                }}>
                                                    {t('eventList.ticketsLeft', { count: remaining })}
                                                </div>
                                            )}
                                        </div>
                                        <Card.Content>
                                            <Card.Header style={{ color: isSoldOut ? '#999' : 'var(--colour_menu_main)' }}>
                                                {event.name}
                                            </Card.Header>
                                            {event.next_instance?.date_time && (
                                                <Card.Meta>
                                                    {moment(event.next_instance.date_time).format('ddd D MMM YYYY, HH:mm')}
                                                </Card.Meta>
                                            )}
                                            {event.location && <Card.Meta>{event.location}</Card.Meta>}
                                            {event.category && (
                                                <div style={{ marginTop: '0.4rem' }}>
                                                    <Label size="tiny" style={{ backgroundColor: 'var(--colour_menu_main)', color: '#fff' }}>
                                                        {event.category}
                                                    </Label>
                                                </div>
                                            )}
                                            <Card.Description style={{
                                                marginTop: '0.5rem', overflow: 'hidden',
                                                display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                                            }}>
                                                {event.description}
                                            </Card.Description>
                                        </Card.Content>
                                        <Card.Content extra style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Label style={{
                                                backgroundColor: isSoldOut ? '#ccc' : 'var(--colour_heading_success)',
                                                color: '#fff', fontWeight: 700,
                                            }}>
                                                {isSoldOut ? t('eventList.soldOut') : formatMoney(event.ticket_price)}
                                            </Label>
                                            {event.instance_count > 1 && (
                                                <span style={{ fontSize: '0.85rem', color: '#888' }}>
                                                    {t('eventList.datesCount', { count: event.instance_count })}
                                                </span>
                                            )}
                                            <Button size="small" style={{
                                                backgroundColor: isSoldOut ? '#ccc' : 'var(--colour_menu_main)',
                                                color: '#fff',
                                            }}>
                                                {isSoldOut ? t('eventList.view') : t('eventList.getTickets')}
                                            </Button>
                                        </Card.Content>
                                    </Card>
                                </Grid.Column>
                            );
                        })}
                    </Grid>
                </>
            )}
        </Container>
    );
}

export default EventList;
